import ErrorMessage from '@/components/errorMessage';
import ReadOnlyField from '@/components/readOnlyField';
import { useApiContext } from '@/contexts/apiContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, FormLabel, LinearProgress, Stack, Typography, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const questions: any = {
  qna: 'Conversational bot that can answer questions based on my documents.',
  extractSummary: 'Extract summary from my documents.',
  multiModal: 'Group, analyze, and summarize different file types (Text, Image, Audio, Video).',
  extractInfo: 'Extract structured information from my documents.',
  azureBlob: 'Azure Blob Storage.',
  awsS3: 'AWS S3.',
  googledrive: 'Google Drive.',
  mssite: 'Microsoft Site (Sharepoint).',
  sql: 'SQL Database.',
  existingDatasource: 'I will use an existing data source.',
  userUpload: 'I will upload files here.',
  builtIn: 'Use built-in free tier (limited use).',
  openai: 'Open AI.',
  azureopenai: 'Azure Open AI.',
  geminiai: 'Gemini AI.',
  claudeai: 'Claude AI.',
  jsonSample: 'I have a sample JSON data of what I want to extract.',
  jsonSchema: 'I have a JSON schema defining what I want to extract.',
  entityNames: 'I would like to extract entities by name.',
  entityType: 'I would like to extract entities by type.',
  externalApi: 'Send extracted information to an external API.',
  customPrompt: 'Apply custom prompts to extend/refine/restructure.',
  multiLanguage: 'The documents are in different languages and I want to analyze them together.',
  imageInDocuments: 'The documents contain images and I want to extract information from them.',
};
type Props = {
  surveyId: string;
  userResponses: Record<string, any>;
  maxHeight: number;
  setParsedRequirements: (parsedRequirements: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepReviewReq = ({
  surveyId,
  userResponses,
  maxHeight,
  onBack,
  onCancel,
  onNext,
  setParsedRequirements,
}: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [mainRequirements, setMainRequirements] = useState<string>('');
  const [extractionMethods, setExtractionMethods] = useState<string>('');
  const [dataSourceType, setDataSourceType] = useState<string>('');
  const [llmOption, setLlmOption] = useState<string>('');
  const [postProcessingActions, setPostProcessingActions] = useState<string>('');

  const [tryAgain, setTryAgain] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const processReqDescription = async () => {
      try {
        setLoading(true);
        const response = await apiContext.submitUserRequirements(surveyId, userResponses.userRequirements);
        setParsedRequirements(response);
        if (response.mainRequirements) {
          setMainRequirements(`${response.mainRequirements}. ${response.documentProps}. ${response.sourceLanguages}`);

          if (response.extractionType) {
            let extractionMethods = response.extractionType;
            if (response.extractionType === 'entityNames') {
              extractionMethods = `${extractionMethods} Entity Names: ${(response.entityNames || []).join(',')}.`;
              extractionMethods = `${extractionMethods} Attribute Names: ${(response.attributeNames || []).join(',')}.`;
            } else if (response.extractionType === 'entityType') {
              extractionMethods = `${extractionMethods} Entity Type: ${response.entityType}.`;
              extractionMethods = `${extractionMethods} Attribute Names: ${(response.attributeNames || []).join(',')}.`;
            }
            setExtractionMethods(extractionMethods);
          }

          if (response.dataSourceType && response.dataSourceType !== 'userUpload') {
            setDataSourceType(response.dataSourceType);
          }

          if (response.llmOption && response.llmOption !== 'builtIn') {
            setLlmOption(response.llmOption);
          }

          if (response.postProcessingActions) {
            setPostProcessingActions(response.postProcessingActions.join(','));
          }
        }
        setTryAgain(false);
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    if (userResponses.userRequirements && tryAgain) {
      processReqDescription();
    }
  }, [userResponses.userRequirements, tryAgain]);

  useEffect(() => {
    const parsedRequirements = userResponses.parsedRequirements;
    if (parsedRequirements && questions) {
      if (parsedRequirements.mainRequirements) {
        let mainRequirements = `${parsedRequirements.mainRequirements.map((mr: string) => questions[mr] || mr).join(' ')}`;
        if (parsedRequirements.documentProps) {
          mainRequirements = `${mainRequirements} ${parsedRequirements.documentProps.map((dp: string) => questions[dp] || dp).join(' ')}`;
        }
        if (parsedRequirements.sourceLanguages) {
          mainRequirements = `${mainRequirements} Source languages: ${parsedRequirements.sourceLanguages.join(', ')}`;
        }
        setMainRequirements(mainRequirements);

        if (parsedRequirements.extractionType) {
          let extractionMethods = questions[parsedRequirements.extractionType] || parsedRequirements.extractionType;
          if (parsedRequirements.extractionType === 'entityNames') {
            extractionMethods = ` ${extractionMethods} Entity Names: ${(parsedRequirements.entityNames || []).join(',')}.`;
            extractionMethods = ` ${extractionMethods} Attribute Names: ${(parsedRequirements.attributeNames || []).join(',')}.`;
          } else if (parsedRequirements.extractionType === 'entityType') {
            extractionMethods = ` ${extractionMethods} Entity Type: ${parsedRequirements.entityType}.`;
            extractionMethods = ` ${extractionMethods} Attribute Names: ${(parsedRequirements.attributeNames || []).join(',')}.`;
          }
          setExtractionMethods(extractionMethods);
        }

        if (parsedRequirements.dataSourceType && parsedRequirements.dataSourceType !== 'userUpload') {
          setDataSourceType(questions[parsedRequirements.dataSourceType] || parsedRequirements.dataSourceType);
        }

        if (parsedRequirements.llmOption && parsedRequirements.llmOption !== 'builtIn') {
          setLlmOption(questions[parsedRequirements.llmSettings.LLM_NAME] || parsedRequirements.llmOption);
        }

        if (parsedRequirements.postProcessingActions) {
          setPostProcessingActions(
            parsedRequirements.postProcessingActions.map((pa: string) => questions[pa]).join(' '),
          );
        }
      }
    }
  }, [userResponses.parsedRequirements, questions]);

  const handleNext = () => {
    onNext();
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {error && <ErrorMessage error={error} />}
        {!error && loading && <Typography>We are analyzing your requirements. Please wait.</Typography>}
        {!error && loading && <LinearProgress color="secondary" />}
        {!loading && mainRequirements && (
          <Stack spacing={2}>
            <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              {'Please review our undestanding of the requirements below and click proceed if you are ok.'}
            </FormLabel>
            <ReadOnlyField label="Your primay requirement is" value={mainRequirements} />
            {extractionMethods && <ReadOnlyField label="You want extract data by" value={extractionMethods} />}
            {dataSourceType && <ReadOnlyField label="Data source to your documents/files is" value={dataSourceType} />}
            {llmOption && <ReadOnlyField label="You would like to use the following LLM" value={llmOption} />}
            {postProcessingActions && (
              <ReadOnlyField label="After processing you would like to" value={postProcessingActions} />
            )}
          </Stack>
        )}
      </Box>
      <Box
        ref={actionPanelRef}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('Change my requirements')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('Proceed')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepReviewReq;
